import { useTranslation } from "react-i18next";
import {
  Bold,
  Code,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  SquareCode,
  Strikethrough,
  type LucideIcon,
} from "lucide-react";
import type { FormatKind } from "../_lib/markdown";

/** Mac shows ⌘, everyone else Ctrl. */
const MOD =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl+";

const BUTTONS: { kind: FormatKind; icon: LucideIcon; shortcut?: string }[] = [
  { kind: "bold", icon: Bold, shortcut: `${MOD}B` },
  { kind: "italic", icon: Italic, shortcut: `${MOD}I` },
  { kind: "strike", icon: Strikethrough, shortcut: `${MOD}Shift+X` },
  { kind: "code", icon: Code, shortcut: `${MOD}E` },
  { kind: "codeBlock", icon: SquareCode },
  { kind: "quote", icon: Quote },
  { kind: "bullet", icon: List },
  { kind: "numbered", icon: ListOrdered },
  { kind: "link", icon: Link2 },
];

/** Markdown buttons over the composer; each acts on the selection. */
export const FormatToolbar = ({ onFormat }: { onFormat: (kind: FormatKind) => void }) => {
  const { t } = useTranslation("chat");
  return (
    <div
      role="toolbar"
      aria-label={t("format.toolbar")}
      className="mb-1.5 flex items-center gap-0.5 overflow-x-auto rounded-xl border border-border bg-surface px-1 py-1 shadow-xs"
    >
      {BUTTONS.map(({ kind, icon: Icon, shortcut }, i) => {
        const label = t(`format.${kind}`);
        return (
          <span key={kind} className="contents">
            {/* Groups: inline marks · blocks · link. */}
            {i === 4 || i === 8 ? <span aria-hidden className="mx-1 h-4 w-px shrink-0 bg-border" /> : null}
            <button
              type="button"
              // Keeps the textarea's selection: the button acts on it.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onFormat(kind)}
              aria-label={label}
              title={shortcut ? `${label} (${shortcut})` : label}
              className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
            >
              <Icon className="size-4" />
            </button>
          </span>
        );
      })}
    </div>
  );
};
